import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { borderColor, borderRadius, flex, minWidth, space, themeGet } from 'styled-system';

import { textTransform } from '../../../theme/system-utilities';
import { BaseSelect, outlineSelectProps } from '../../Select';

interface Option {
  value: string;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
  selected?: boolean;
}

interface SelectInputProps extends ComponentProps<typeof BaseSelect> {
  options: Option[];
}

const errorStyles = props =>
  props.hasError &&
  css`
    color: ${props.theme.colors.sys.error.text.default};
    :valid {
      border-color: ${props.theme.colors.sys.error.text.default};
    }
  `;

const StyledSelect = styled(BaseSelect)`
  ${space};
  ${minWidth};
  ${borderRadius};
  ${borderColor};
  ${flex};
  ${textTransform};

  :not(:valid) {
    color: ${themeGet('colors.sys.neutral.text.default')};
  }

  :valid {
    border-color: ${themeGet('colors.sys.neutral.border.active')};
  }

  :focus-visible:valid {
    border-color: ${themeGet('colors.sys.secondary.border.focus')};
  }

  ${errorStyles};
`;

const selectStyles = {
  minWidth: '120px',
  maxWidth: '180px',
  borderRadius: 'small',
  flex: [1, 0],
  textTransform: 'capitalize',
};

const SelectInput = ({ options, ...props }: SelectInputProps) => {
  return (
    <StyledSelect {...outlineSelectProps} {...selectStyles} {...props}>
      {options.map(({ value, label, ...optionProps }) => (
        <option key={value} value={value} {...optionProps}>
          {label}
        </option>
      ))}
    </StyledSelect>
  );
};

export default SelectInput;
